import { useMemo } from 'react';

import { useMemberId } from '@trello/authentication';
import { sortBoards } from '@trello/boards';
import { useMemberInboxIds } from '@trello/personal-workspace';
import { useRecentBoards } from '@trello/recent-boards';
import { useWorkspace } from '@trello/workspaces';

import { useBoardSwitcherMemberFragment } from './BoardSwitcherMemberFragment.generated';
import { useBoardSwitcherWorkspaceFragment } from './BoardSwitcherWorkspaceFragment.generated';
import { isRealWorkspace } from './isRealWorkspace';

export const useBoardSwitcher = () => {
  const memberId = useMemberId();
  const { workspaceId } = useWorkspace();

  const { idBoard: inboxBoardId, idOrganization: personalWorkspaceId } =
    useMemberInboxIds();

  const { data: recentData, loading: recentLoading } = useRecentBoards();

  const recentBoards = useMemo(() => recentData?.boards ?? [], [recentData]);

  const { data } = useBoardSwitcherMemberFragment({
    from: { id: memberId },
    returnPartialData: true,
  });

  const fragmentId =
    isRealWorkspace(workspaceId) && workspaceId !== personalWorkspaceId
      ? workspaceId
      : null;

  const { data: currentWorkspace } = useBoardSwitcherWorkspaceFragment({
    from: { id: fragmentId },
  });

  const organizations = useMemo(() => {
    return data?.organizations ?? [];
  }, [data?.organizations]);

  const memberWorkspaceIds = useMemo(() => {
    return organizations
      .filter((workspace) => workspace.id !== personalWorkspaceId)
      .sort((a, b) => {
        const aName = a.displayName ?? a.name ?? '';
        const bName = b.displayName ?? b.name ?? '';
        return aName.localeCompare(bName);
      })
      .map(({ id }) => id);
  }, [organizations, personalWorkspaceId]);

  const starredBoards = useMemo(() => {
    const boardStars = data?.boardStars ?? [];
    const filteredBoardStars = boardStars.filter(({ idBoard }) =>
      data?.boards?.some(({ id }) => id === idBoard),
    );
    return [...filteredBoardStars]
      .sort((a, b) => {
        return a.pos - b.pos;
      })
      .filter(({ idBoard }) => idBoard !== inboxBoardId);
  }, [data?.boardStars, data?.boards, inboxBoardId]);

  const workspaceBoards = useMemo(() => {
    const map = new Map<string, string[]>();

    if (!data?.boards?.length) {
      return map;
    }

    type Board = (typeof data.boards)[number];
    const boardMap = new Map<string, Board>();

    data.boards.forEach((board) => {
      boardMap.set(board.id, board);
    });

    const starredBoardModels = starredBoards.reduce((acc, { idBoard }) => {
      const board = boardMap.get(idBoard);
      if (board) {
        acc.push(board);
      }
      return acc;
    }, [] as Board[]);

    const allSortedBoards = sortBoards({
      boards: data.boards,
      myStarredBoardIds: starredBoards.map(({ idBoard }) => idBoard),
    });

    // Using a Set to maintain the sort order of the starred boards
    const sortedBoards = new Set([...starredBoardModels, ...allSortedBoards]);

    sortedBoards.forEach(({ id, idOrganization }) => {
      if (id === inboxBoardId) {
        return;
      }

      if (!idOrganization || idOrganization === personalWorkspaceId) {
        return;
      }

      const boards = map.get(idOrganization) ?? [];
      map.set(idOrganization, [...boards, id]);
    });

    return map;
  }, [data, inboxBoardId, personalWorkspaceId, starredBoards]);

  const guestWorkspaceIds = useMemo(() => {
    // key: workspaceId, value: workspace displayName
    const map = new Map<string, string>();

    const boards = data?.boards ?? [];

    boards.forEach(({ idOrganization, organization }) => {
      if (
        idOrganization &&
        !memberWorkspaceIds.includes(idOrganization) &&
        idOrganization !== personalWorkspaceId &&
        organization?.displayName
      ) {
        map.set(idOrganization, organization?.displayName);
      }
    });

    return Array.from(map.keys()).sort((a, b) => {
      const aName = map.get(a) ?? '';
      const bName = map.get(b) ?? '';
      return aName.localeCompare(bName);
    });
  }, [data?.boards, personalWorkspaceId, memberWorkspaceIds]);

  const sortedWorkspaceIds = useMemo(() => {
    if (!workspaceId || workspaceId === personalWorkspaceId) {
      return [...memberWorkspaceIds, ...guestWorkspaceIds];
    }

    return [
      workspaceId,
      ...memberWorkspaceIds.filter((id) => id !== workspaceId),
      ...guestWorkspaceIds.filter((id) => id !== workspaceId),
    ];
  }, [workspaceId, personalWorkspaceId, memberWorkspaceIds, guestWorkspaceIds]);

  return {
    currentWorkspace,
    recentBoards,
    recentLoading,
    starredBoards,
    workspaceBoards,
    memberWorkspaceIds,
    guestWorkspaceIds,
    sortedWorkspaceIds,
  };
};
